import axios from "axios";
import { logout } from "../../redux/action/logout-action";
import { logoutUser } from "../../redux/reducers/userDataSlice";
import { Store, persistor } from "../../redux/store/store";
import { clearAllStorage } from "../utils/clear-all-storage";
import { authLoadingManager } from "../loader/auth-loading-manager";
import { authReloadDetector } from "../utils/auth-reload-detector";
import { getGuestHomePath } from "../utils/home-path";
const LOG_STORAGE_KEY = "errorLogged";

declare global {
  interface Window {
    __ERROR_LOGGER_SETUP__?: boolean;
  }
}
function hasErrorBeenLogged() {
  return sessionStorage.getItem(LOG_STORAGE_KEY) === "true";
}
function markErrorAsLogged() {
  sessionStorage.setItem(LOG_STORAGE_KEY, "true");
}
const errorapiUrl = process.env.REACT_APP_VIRTCO_ERROR_LOGGER;

/**
 * Backend expects `requestPayload` as a JSON object (JObject), not a primitive or JSON string.
 * Axios often stores `config.data` as a stringified body, which would deserialize as JValue.
 */
export function normalizeRequestPayload(data: unknown): Record<string, unknown> {
  if (data == null || data === "") return {};
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return {};
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      if (Array.isArray(parsed)) {
        return { items: parsed };
      }
      return { value: parsed };
    } catch {
      return { rawBody: data };
    }
  }
  if (typeof data === "object") {
    if (data instanceof FormData) {
      return { _type: "FormData", note: "payload omitted" };
    }
    if (Array.isArray(data)) {
      return { items: data };
    }
    return { ...(data as Record<string, unknown>) };
  }
  return { value: data };
}

/** Backend may expect JObject; strings (e.g. stack trace) must be wrapped. */
function normalizeJsonMessage(msg: unknown): Record<string, unknown> {
  if (msg == null) return {};
  if (typeof msg === "string") {
    const t = msg.trim();
    if (!t) return {};
    try {
      const parsed: unknown = JSON.parse(t);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return { detail: t };
    } catch {
      return { detail: msg };
    }
  }
  if (typeof msg === "object" && !Array.isArray(msg)) {
    return { ...(msg as Record<string, unknown>) };
  }
  return { value: msg as unknown };
}

type ClientErrorLogPayload = {
  name: string;
  url: string;
  status: string;
  statusText: string;
  message: string;
  jsonMessage: unknown;
  httpMethod: string;
  requestPayload?: unknown;
};

/** Backend model uses PascalCase JSON (Name, Message, …) — camelCase does not bind. */
function toApiErrorDto(logData: ClientErrorLogPayload) {
  const name = String(logData.name ?? "").trim() || "Client Error";
  const message = String(logData.message ?? "").trim() || "(no message)";
  return {
    Name: name,
    Url: String(logData.url ?? ""),
    Status: String(logData.status ?? ""),
    StatusText: String(logData.statusText ?? ""),
    Message: message,
    JsonMessage: normalizeJsonMessage(logData.jsonMessage),
    HttpMethod: String(logData.httpMethod ?? "UNKNOWN"),
  };
}

export async function sendLogToBackend(logData: ClientErrorLogPayload) {
  const { requestPayload } = logData;
  const dto = toApiErrorDto(logData);
  const rp = normalizeRequestPayload(requestPayload);
  /**
   * Some deployments validate Name/Message on the root model; others expect nested errorDto.
   * Send both shapes + both payload casings so Newtonsoft / STJ bindings accept the request.
   */
  const body = {
    ...dto,
    errorDto: dto,
    ErrorDto: dto,
    requestPayload: rp,
    RequestPayload: rp,
  };
  try {
    await axios.post(errorapiUrl!, body, {
      headers: { "Content-Type": "application/json" },
    });
    markErrorAsLogged();
  } catch (err) {
    console.error("Failed to send log:", err);
  }
}
let unauthorizedLogoutInFlight: Promise<void> | null = null;

/**
 * Leftover 401s after an intentional logout must not be treated as session expiry.
 * Talent Pool header/dashboard still have in-flight calls when tokens are cleared.
 */
export function shouldSuppressUnauthorizedLogout(): boolean {
  if (
    authLoadingManager.isLogoutUiTransitionActive() ||
    authLoadingManager.isLogoutButtonLoaderPhase()
  ) {
    return true;
  }

  try {
    const loginData = Store.getState()?.userDataSlice?.loginData;
    const hasStoredAccessToken = Boolean(localStorage.getItem("AccessToken"));
    const hasReduxAccessToken = Boolean(loginData?.accessToken);
    return !hasStoredAccessToken && !hasReduxAccessToken;
  } catch {
    return false;
  }
}

async function performUnauthorizedLogout(): Promise<void> {
  if (shouldSuppressUnauthorizedLogout()) {
    return;
  }

  authReloadDetector.markTokensCleared();

  try {
    Store.dispatch(logout());
    Store.dispatch(logoutUser());
    try {
      await persistor.purge();
    } catch {
      /* continue logout even if persist purge fails */
    }
    await clearAllStorage();
    sessionStorage.setItem("sessionExpired", "true");
  } catch (err) {
    console.error("Error during session logout:", err);
  }

  window.location.href = getGuestHomePath();
}

/** Clears all storage/cookies and sends the user to the home page. */
export function handleUnauthorized(): void {
  if (shouldSuppressUnauthorizedLogout()) {
    return;
  }
  if (!unauthorizedLogoutInFlight) {
    unauthorizedLogoutInFlight = performUnauthorizedLogout().finally(() => {
      unauthorizedLogoutInFlight = null;
    });
  }
}
/** Suppress known third-party errors (e.g. browser extensions running at document-start before body exists). */
function isThirdPartyDocumentReadyError(reason: unknown): boolean {
  const msg = reason instanceof Error ? reason.message : String(reason ?? "");
  return (
    msg.includes("Document not ready") ||
    msg.includes("no body") ||
    msg.includes("host-additional-hook") ||
    (msg.includes("document-start") && msg.includes("loading"))
  );
}

function formatConsoleArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === "string") return a;
      if (a instanceof Error) return `${a.name}: ${a.message} ${a.stack ?? ""}`;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
}

/** Collect every substring we can from console args (Jam/MST often splits message across args). */
function collectConsoleArgText(args: unknown[]): string {
  const chunks: string[] = [];
  for (const a of args) {
    if (a === undefined || a === null) continue;
    if (typeof a === "string") {
      chunks.push(a);
      continue;
    }
    if (a instanceof Error) {
      chunks.push(a.name, a.message, a.stack ?? "");
      continue;
    }
    try {
      chunks.push(JSON.stringify(a));
    } catch {
      chunks.push(String(a));
    }
  }
  return chunks.join(" ");
}

/** True if any console argument (or their combination) matches ignorable patterns. */
function shouldIgnoreConsoleArgs(args: unknown[]): boolean {
  const combined = collectConsoleArgText(args);
  if (isIgnorableConsoleNoise(combined)) return true;
  for (const a of args) {
    if (typeof a === "string" && isIgnorableConsoleNoise(a)) return true;
    if (a instanceof Error && isIgnorableConsoleNoise(`${a.message} ${a.stack ?? ""}`)) return true;
  }
  return false;
}

/** Extension / DevTools noise — not actionable in app code; avoid console + error API spam */
function isIgnorableConsoleNoise(message: string): boolean {
  if (!message || typeof message !== "string") return false;
  const m = message;
  return (
    m.includes("jamToggleDumpStore") ||
    m.includes("mobx-state-tree") ||
    m.includes("injectionLifecycle") ||
    // Jam / injector MST paths (shown as sw.js:1 in DevTools)
    m.includes("iframe-jam-ui") ||
    m.includes("host-additional-hooks") ||
    m.includes("Path upon death: '/tabStates/") ||
    (m.includes("/tabStates/") && m.includes("no longer part of a state tree")) ||
    m.includes("extension port") ||
    m.includes("message channel is closed") ||
    m.includes("back/forward cache") ||
    m.includes("runtime.lastError") ||
    // SignalR / Azure hub — noisy reconnect paths (LongPolling avoids SSE POST 404)
    m.includes("ClientTimeoutInterval") ||
    m.includes("No Connection with that ID") ||
    (m.includes("Status code '404'") && m.includes("chatHub")) ||
    m.includes("Connection disconnected with error") ||
    m.includes("EventSource failed to connect") ||
    m.includes("Failed to start the transport 'ServerSentEvents'") ||
    // Library logs transport fallback (WebSockets → LongPolling) before connect succeeds — not worth console/API spam
    (m.includes("Failed to start the transport") &&
      (m.includes("WebSockets") || m.includes("'WebSockets'"))) ||
    m.includes("WebSocket failed to connect") ||
    m.includes("the connection could not be found on the server") ||
    m.includes("Unable to connect to the server with any of the available transports") ||
    m.includes("AggregateErrors: Unable to connect") ||
    (m.includes("LongPolling failed") && m.includes("No Connection with that ID")) ||
    // Webpack dev server HMR client (wss://localhost:4200/ws) — not app SignalR; Cursor/IDE preview often shows "Invalid frame header"
    (m.includes("WebSocket connection to") &&
      (m.includes("/ws") || m.includes("localhost:4200")) &&
      (m.includes("Invalid frame header") || m.includes("failed"))) ||
    (m.includes("non-serializable value was detected") &&
      m.includes("persist/PURGE")) ||
    m.includes("Download the React DevTools") ||
    m.includes("react-devtools") ||
    (m.includes("webpack-dev-server") && m.includes("Warnings while compiling"))
  );
}

export function setupGlobalErrorLogging() {
  if (window.__ERROR_LOGGER_SETUP__) return;
  window.__ERROR_LOGGER_SETUP__ = true;

  window.addEventListener("unhandledrejection", (event) => {
    if (isThirdPartyDocumentReadyError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const reasonText =
      event.reason instanceof Error
        ? `${event.reason.message}\n${event.reason.stack ?? ""}`
        : String(event.reason ?? "");
    if (isIgnorableConsoleNoise(reasonText)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });

  window.onerror = function (message, source, lineno, colno, error) {
    if (typeof message === "string" && isThirdPartyDocumentReadyError(message)) return true;
    const msgStr = typeof message === "string" ? message : String(message ?? "");
    const withStack = error ? `${msgStr}\n${error.stack ?? ""}` : msgStr;
    if (isIgnorableConsoleNoise(msgStr) || isIgnorableConsoleNoise(withStack)) return true;
    if (!hasErrorBeenLogged()) {
      sendLogToBackend({
        name: "Uncaught JavaScript Error",
        url: source || "Unknown Source",
        status: "Client-Side Error",
        statusText: `Line: ${lineno}, Column: ${colno}`,
        message: msgStr || String(message ?? ""),
        jsonMessage: error ? error.stack : "No stack trace",
        httpMethod: "N/A",
        requestPayload: {},
      });
    }
  };
  console.error = (function (originalConsoleError) {
    return function (...args) {
      const message = formatConsoleArgs(args);
      const isFedCMOrGSIError =
        typeof message === "string" &&
        (message.includes("FedCM") ||
          message.includes("GSI_LOGGER") ||
          (message.includes("AbortError") && message.includes("signal is aborted")) ||
          (message.includes("Error retrieving a token") && message.includes("FedCM")));
      if (isFedCMOrGSIError) {
        return; // Suppress: FedCM/GSI errors when FedCM is disabled; use GSI popup flow instead
      }
      if (shouldIgnoreConsoleArgs(args)) {
        return; // Extensions / known hub transport noise — not sent to error API
      }
      originalConsoleError(...args);
      sendLogToBackend({
        name: "Console Error",
        url: window.location.href,
        status: "Console Error",
        statusText: "Check DevTools Console",
        message,
        jsonMessage: {},
        httpMethod: "N/A",
        requestPayload: {},
      });
    };
  })(console.error);
  console.warn = (function (originalConsoleWarn) {
    return function (...args) {
      const message = formatConsoleArgs(args);
      const isReactLifecycleWarning =
        typeof message === "string" &&
        (message.includes("componentWillReceiveProps") ||
          message.includes("componentWillMount") ||
          message.includes("componentWillUpdate") ||
          message.includes("UNSAFE_"));
      const isFedCMWarning =
        typeof message === "string" &&
        (message.includes("FedCM") ||
          message.includes("FedCM was disabled") ||
          message.includes("third-party sign-in"));
      if (isReactLifecycleWarning || isFedCMWarning) {
        return; // Suppress: lifecycle from deps; FedCM is a browser/settings notice, GSI continues without it
      }
      if (shouldIgnoreConsoleArgs(args)) {
        return;
      }
      originalConsoleWarn(...args);
      sendLogToBackend({
        name: "Console Warning",
        url: window.location.href,
        status: "Console Warning",
        statusText: "Check DevTools Console",
        message,
        jsonMessage: {},
        httpMethod: "N/A",
        requestPayload: {},
      });
    };
  })(console.warn);
}
setupGlobalErrorLogging();